import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import BlogManager from "@/components/admin/BlogManager";
import { MediaManager } from "@/components/admin/MediaLibrary";
import ContactsManager from "@/components/admin/ContactsManager";
import CategoriesManager from "@/components/admin/CategoriesManager";
import UsersManager from "@/components/admin/UsersManager";
import SecurityManager from "@/components/admin/SecurityManager";
import Overview from "@/components/admin/Overview";
import SiteSettingsManager from "@/components/admin/SiteSettingsManager";
import ActivityManager from "@/components/admin/ActivityManager";
import { canManagePlatform } from "@/lib/admin-overview";
import { useSearchParams } from "react-router-dom";
import { FileText, Tags, Image as ImageIcon, Inbox, LogOut, Users, ShieldCheck, ExternalLink, ChevronRight, ChevronDown, Eye, EyeOff, GripVertical, Settings2, Check, RotateCcw } from "lucide-react";
import { useNavPrefs, sortByOrder } from "@/components/admin/useNavPrefs";
import {
  Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent, SidebarGroupLabel,
  SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarProvider, SidebarTrigger,
} from "@/components/ui/sidebar";

// Add new sections here: each group becomes a block in the sidebar.
const NAV = [
  { label: "Painel", items: [
    { id: "overview", title: "Visão geral", desc: "Resumo da operação editorial.", icon: FileText },
  ] },
  { label: "Blog", items: [
    { id: "articles", title: "Artigos", desc: "Crie, edite, agende e publique artigos.", icon: FileText },
    { id: "categories", title: "Categorias", desc: "Organize os temas do blog.", icon: Tags },
  ] },
  { label: "Conteúdo", items: [
    { id: "media", title: "Biblioteca de mídia", desc: "Imagens enviadas para o site e artigos.", icon: ImageIcon },
  ] },
  { label: "Leads", items: [
    { id: "logs", title: "Contatos recebidos", desc: "Registros dos formulários de contato.", icon: Inbox, managers: true },
  ] },
  { label: "Configurações", items: [
    { id: "site", title: "Configurações do site", desc: "Contatos e redes sociais da Abillyting.", icon: Settings2, managers: true },
    { id: "activity", title: "Registro de atividades", desc: "Histórico editorial e administrativo a partir da ativação.", icon: ShieldCheck, managers: true },
    { id: "users", title: "Utilizadores", desc: "Quem tem acesso ao painel e o status do 2FA.", icon: Users, managers: true },
    { id: "security", title: "Minha segurança", desc: "Autenticação em dois fatores e senha da sua conta.", icon: ShieldCheck },
  ] },
];

const Admin = () => {
  const { toast } = useToast();
  const [session, setSession] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [role, setRole] = useState<string | null>(null);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaFactor, setMfaFactor] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const [params, setParams] = useSearchParams();
  const { prefs, save, reset } = useNavPrefs(session?.user?.id);
  const [editing, setEditing] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const section = params.get("s") ?? "overview";
  const setSection = (id: string) => setParams({ s: id });

  const checkMfa = async () => {
    const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (data && data.nextLevel === "aal2" && data.currentLevel !== "aal2") {
      const { data: f } = await supabase.auth.mfa.listFactors();
      setMfaFactor(f?.totp.find((x) => x.status === "verified")?.id ?? null);
    } else setMfaFactor(null);
  };

  const verifyMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaFactor) return;
    const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId: mfaFactor, code: mfaCode.trim() });
    if (error) return toast({ title: "Código inválido", description: "Confira o código no aplicativo.", variant: "destructive" });
    setMfaCode(""); setMfaFactor(null);
  };

  const checkAdmin = async (userId: string) => {
    await checkMfa();
    const { data } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId);
    const roles = (data ?? []).map((r) => r.role as string);
    const r = ["superadmin", "admin", "editor"].find((x) => roles.includes(x)) ?? null;
    setRole(r);
    setIsAdmin(!!r);
  };

  useEffect(() => {
    document.title = "Admin - Abillyting Marketing";
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => {
      setSession(s);
      if (s?.user) checkAdmin(s.user.id);
      else setIsAdmin(false);
    });
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      if (s?.user) checkAdmin(s.user.id).finally(() => setChecking(false));
      else setChecking(false);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) toast({ title: "Falha no login", description: error.message, variant: "destructive" });
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  if (checking) return <div className="p-8">Carregando...</div>;

  if (!session) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Login Admin</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <Input type="email" placeholder="E-mail" value={email} onChange={(e) => setEmail(e.target.value)} required />
              <Input type="password" placeholder="Senha" value={password} onChange={(e) => setPassword(e.target.value)} required />
              <Button type="submit" className="w-full">Entrar</Button>
            </form>
          </CardContent>
        </Card>
      </main>
    );
  }

  if (mfaFactor) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader><CardTitle>Verificação em dois fatores</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={verifyMfa} className="space-y-4">
              <p className="text-sm text-muted-foreground">Digite o código de 6 dígitos do seu aplicativo autenticador.</p>
              <Input autoFocus inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="000000" value={mfaCode} onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))} className="text-center tracking-[0.5em] text-lg" />
              <Button type="submit" className="w-full" disabled={mfaCode.length !== 6}>Verificar</Button>
              <Button type="button" variant="ghost" className="w-full" onClick={handleLogout}>Usar outra conta</Button>
            </form>
          </CardContent>
        </Card>
      </main>
    );
  }

  if (!isAdmin) {
    return (
      <main className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md">
          <CardContent className="pt-6 space-y-4">
            <p>Sua conta não tem acesso ao painel.</p>
            <Button variant="outline" onClick={handleLogout}>Sair</Button>
          </CardContent>
        </Card>
      </main>
    );
  }

  const isManager = canManagePlatform(role);
  const nav = NAV.map((g) => ({ ...g, items: g.items.filter((i: any) => !i.managers || isManager) })).filter((g) => g.items.length);
  const current = nav.flatMap((g) => g.items).find((i) => i.id === section) ?? nav[0].items[0];
  const group = nav.find((g) => g.items.some((i) => i.id === current.id)) ?? nav[0];
  const hiddenItems = nav.flatMap((g) => g.items).filter((i) => prefs.hidden.includes(i.id));
  const toggleHidden = (id: string) =>
    save({ ...prefs, hidden: prefs.hidden.includes(id) ? prefs.hidden.filter((x) => x !== id) : [...prefs.hidden, id] });
  const dropOn = (groupIds: string[], targetId: string) => {
    if (!dragId || dragId === targetId || !groupIds.includes(dragId)) { setDragId(null); setOverId(null); return; }
    const all = nav.flatMap((g) => sortByOrder(g.items, prefs.order).map((i) => i.id)).filter((id) => id !== dragId);
    all.splice(all.indexOf(targetId), 0, dragId);
    save({ ...prefs, order: all });
    setDragId(null); setOverId(null);
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-muted/30">
        <Sidebar collapsible="icon">
          <SidebarHeader className="border-b border-sidebar-border">
            <div className="flex items-center gap-2 px-2 py-1.5">
              <div className="h-8 w-8 shrink-0 rounded-lg bg-primary text-primary-foreground flex items-center justify-center font-heading font-bold">A</div>
              <div className="group-data-[collapsible=icon]:hidden leading-tight">
                <p className="font-heading font-semibold text-sm">Abillyting</p>
                <p className="text-xs text-muted-foreground">Painel administrativo</p>
              </div>
            </div>
          </SidebarHeader>
          <SidebarContent>
            <div className="px-3 pt-2 flex items-center justify-between group-data-[collapsible=icon]:hidden">
              {isManager && <Button variant="ghost" size="sm" onClick={() => setEditing(!editing)} className="text-xs flex items-center gap-1 text-muted-foreground hover:text-primary">
                {editing ? <Check className="h-3.5 w-3.5" /> : <Settings2 className="h-3.5 w-3.5" />}
                {editing ? "Concluir" : "Personalizar menu"}
              </Button>}
              {isManager && editing && <Button variant="ghost" size="sm" onClick={reset} className="text-xs flex items-center gap-1 text-muted-foreground hover:text-primary"><RotateCcw className="h-3 w-3" />Restaurar</Button>}
            </div>
            {nav.map((g) => {
              const items = sortByOrder(g.items, prefs.order).filter((it) => editing || !prefs.hidden.includes(it.id));
              if (!items.length) return null;
              return (
                <SidebarGroup key={g.label}>
                  <SidebarGroupLabel>{g.label}</SidebarGroupLabel>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {items.map((it) => {
                        const hidden = prefs.hidden.includes(it.id);
                        return (
                          <SidebarMenuItem
                            key={it.id}
                            draggable={editing}
                            onDragStart={() => setDragId(it.id)}
                            onDragOver={(e) => { if (editing && dragId) { e.preventDefault(); setOverId(it.id); } }}
                            onDragLeave={() => setOverId(null)}
                            onDrop={() => dropOn(g.items.map((x) => x.id), it.id)}
                            onDragEnd={() => { setDragId(null); setOverId(null); }}
                            className={`${overId === it.id && dragId !== it.id ? "border-t-2 border-primary" : ""} ${dragId === it.id ? "opacity-40" : ""}`}
                          >
                            <SidebarMenuButton isActive={current.id === it.id} tooltip={it.title} onClick={() => !editing && setSection(it.id)} className={`${editing ? "cursor-grab" : ""} ${hidden ? "opacity-50" : ""}`}>
                              {editing ? <GripVertical className="h-4 w-4 text-muted-foreground" /> : <it.icon className="h-4 w-4" />}
                              <span className="flex-1">{it.title}</span>
                              {editing && (
                                <span role="button" aria-label={hidden ? "Mostrar" : "Ocultar"} onClick={(e) => { e.stopPropagation(); toggleHidden(it.id); }} className="p-0.5 rounded hover:text-primary">
                                  {hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </span>
                              )}
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        );
                      })}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>
              );
            })}
            {!editing && hiddenItems.length > 0 && (
              <SidebarGroup>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton tooltip={`Ocultos (${hiddenItems.length})`} onClick={() => setShowHidden(!showHidden)} className="text-muted-foreground">
                      <EyeOff className="h-4 w-4" />
                      <span className="flex-1">Ocultos ({hiddenItems.length})</span>
                      <ChevronDown className={`h-4 w-4 transition-transform ${showHidden ? "rotate-180" : ""}`} />
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  {showHidden && hiddenItems.map((it) => (
                    <SidebarMenuItem key={it.id}>
                      <SidebarMenuButton isActive={current.id === it.id} tooltip={it.title} onClick={() => setSection(it.id)} className="pl-6">
                        <it.icon className="h-4 w-4" />
                        <span className="flex-1">{it.title}</span>
                        <span role="button" aria-label="Mostrar no menu" title="Mostrar no menu" onClick={(e) => { e.stopPropagation(); toggleHidden(it.id); }} className="p-0.5 rounded hover:text-primary"><Eye className="h-4 w-4" /></span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroup>
            )}
          </SidebarContent>
          <SidebarFooter className="border-t border-sidebar-border">
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="Ver site"><a href="/" target="_blank" rel="noopener noreferrer"><ExternalLink className="h-4 w-4" /><span>Ver site</span></a></SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton tooltip="Sair" onClick={handleLogout}><LogOut className="h-4 w-4" /><span className="truncate">Sair · {session.user?.email}</span></SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarFooter>
        </Sidebar>

        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-14 flex items-center gap-3 border-b border-border bg-background px-4 sticky top-0 z-30">
            <SidebarTrigger />
            <nav className="text-sm text-muted-foreground flex items-center gap-1.5">
              <span>{group.label}</span><ChevronRight size={14} /><span className="text-foreground font-medium">{current.title}</span>
            </nav>
          </header>
          <main className="flex-1 p-6">
            <div className="max-w-7xl mx-auto space-y-6">
              <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                  <h1 className="font-heading text-2xl font-bold">{current.title}</h1>
                  <p className="text-sm text-muted-foreground">{current.desc}</p>
                </div>
              </div>
              {current.id === "overview" && <Overview isManager={isManager} onNavigate={(id) => id === "new-article" ? setParams({ s: "articles", new: "1" }) : setSection(id)} />}
              {current.id === "site" && isManager && <SiteSettingsManager />}
              {current.id === "activity" && isManager && <ActivityManager />}
              {current.id === "articles" && <BlogManager />}
              {current.id === "categories" && <CategoriesManager />}
              {current.id === "media" && <MediaManager />}
              {current.id === "logs" && <ContactsManager />}
              {current.id === "users" && <UsersManager myRole={role} onGoSecurity={() => setSection("security")} />}
              {current.id === "security" && <SecurityManager />}
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default Admin;
